import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Switch, Typography, showToast } from '@attentive-platform/stem-ui';

import StateLozenge from './StateLozenge';
import {
  createIntakeRecord,
  fetchCompanies,
  fetchSalesTeamMembers,
  fetchScopeRecords,
  accountOwnerFieldBody,
  mapCompanyOption,
  mapRecordListRow,
  mapSalesTeam,
  parseFieldErrors,
  resolveRosterValue
} from './api';
import { isValidEmail } from '../../Utils/HelperFunctions';
import { IntakeDrawerStyles as S } from './styles';

const RECORD_TYPES = [
  {
    id: 'pilot',
    title: 'Pilot',
    body: 'Prospect company, AE and FDE. Used while the company is still a pilot.'
  },
  {
    id: 'existing',
    title: 'Existing customer',
    body: 'Registered company, account owner and technical owner. No pilot fields.'
  }
];

const EMPTY_STATE = {
  recordType: 'pilot',
  email: '',
  company: '',
  companyId: null,
  ae: '',
  fde: '',
  accountOwner: '',
  technicalOwner: '',
  cc: '',
  sendEmails: true
};

const EmailsToggle = ({ on, onToggle }) => (
  <S.EmailsToggleRow>
    <S.EmailsToggleCopy>
      <S.EmailsToggleTitle>Send emails to the customer</S.EmailsToggleTitle>
      <S.EmailsToggleHint>
        {on
          ? 'Invite, reopen notices, and submission copies go to the customer email.'
          : 'No customer mail for this record. Internal receipts still go.'}
      </S.EmailsToggleHint>
    </S.EmailsToggleCopy>
    <Switch
      size='small'
      color='primary'
      checked={on}
      onChange={(_e, checked) => onToggle(checked)}
      aria-label='Send emails to the customer'
    />
  </S.EmailsToggleRow>
);

const IntakeDrawer = ({ open, onClose, onSend, onOpenExisting }) => {
  const [form, setForm] = useState(EMPTY_STATE);
  const [team, setTeam] = useState({
    ae: [],
    se: [],
    accountOwners: [],
    technicalOwners: []
  });
  const [teamLoading, setTeamLoading] = useState(false);
  const [blockingRecord, setBlockingRecord] = useState(null);
  const [duplicateCheckPending, setDuplicateCheckPending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);

  const isExisting = form.recordType === 'existing';
  const sendEmails = !!form.sendEmails;

  const companyItems = useMemo(() => {
    const items = companies.map(c => ({
      label: c.name,
      value: c.id == null ? '' : String(c.id)
    }));
    if (selectedCompany?.id && !items.some(item => String(item.value) === String(selectedCompany.id))) {
      items.unshift({
        label: selectedCompany.name,
        value: String(selectedCompany.id)
      });
    }
    return items;
  }, [companies, selectedCompany]);

  const accountOwnerItems = useMemo(() => {
    const items = [...(team.accountOwners || [])];
    const selectedId = form.accountOwner ? String(form.accountOwner) : '';
    if (selectedId && !items.some(item => String(item.value) === selectedId)) {
      const name =
        selectedCompany?.accountOwnerName ||
        selectedCompany?.accountOwnerEmail ||
        'Assigned account owner';
      const role = selectedCompany?.accountOwnerRoleTag;
      items.unshift({
        label: role ? `${name} · ${role}` : name,
        value: selectedId,
        email: selectedCompany?.accountOwnerEmail || '',
        name,
        roleTag: role || 'KAM'
      });
    }
    return items;
  }, [team.accountOwners, form.accountOwner, selectedCompany]);

  const technicalOwnerItems = useMemo(() => {
    const items = [...(team.technicalOwners || [])];
    const selectedId = form.technicalOwner ? String(form.technicalOwner) : '';
    if (selectedId && !items.some(item => String(item.value) === selectedId)) {
      const name =
        selectedCompany?.technicalOwnerName ||
        selectedCompany?.technicalOwnerEmail ||
        'Assigned technical owner';
      items.unshift({
        label: `${name} · TCSM`,
        value: selectedId,
        email: selectedCompany?.technicalOwnerEmail || '',
        name,
        roleTag: 'TCSM'
      });
    }
    return items;
  }, [team.technicalOwners, form.technicalOwner, selectedCompany]);

  const set = patch => {
    setForm(f => ({ ...f, ...patch }));
    setFieldErrors(e => {
      const next = { ...e };
      Object.keys(patch).forEach(k => {
        if (k === 'email') delete next.customer_email;
        if (k === 'company' || k === 'companyId') delete next.company_name;
        if (k === 'companyId') delete next.company_id;
        if (k === 'cc') delete next.cc_emails;
        if (k === 'ae') delete next.account_executive_id;
        if (k === 'fde') delete next.fde_id;
        if (k === 'accountOwner') delete next.kam_id;
        if (k === 'technicalOwner') delete next.technical_csm_id;
        if (k === 'recordType') delete next.record_type;
        if (k === 'recordType') delete next.is_pilot;
      });
      return next;
    });
  };

  const resetDrawer = () => {
    setForm(EMPTY_STATE);
    setFieldErrors({});
    setBlockingRecord(null);
    setDuplicateCheckPending(false);
    setCompanies([]);
    setCompanySearch('');
    setSelectedCompany(null);
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTeamLoading(true);
    fetchSalesTeamMembers().then(({ data, error }) => {
      if (cancelled) return;
      setTeamLoading(false);
      if (error) {
        showToast('Could not load sales team', 'error');
        return;
      }
      setTeam(mapSalesTeam(data));
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // When the roster arrives after a company pick, resolve owners onto Select values
  useEffect(() => {
    if (!open || !isExisting || !selectedCompany) return;
    setForm(f => {
      const nextOwner = resolveRosterValue(selectedCompany.accountOwnerPerson, team.accountOwners);
      const nextTcsm = resolveRosterValue(selectedCompany.technicalOwnerPerson, team.technicalOwners);
      const patch = {};
      const ownerInRoster = team.accountOwners.some(u => String(u.value) === String(f.accountOwner));
      const tcsmInRoster = team.technicalOwners.some(u => String(u.value) === String(f.technicalOwner));
      if (nextOwner && (!f.accountOwner || !ownerInRoster)) patch.accountOwner = nextOwner;
      if (nextTcsm && (!f.technicalOwner || !tcsmInRoster)) patch.technicalOwner = nextTcsm;
      return Object.keys(patch).length ? { ...f, ...patch } : f;
    });
  }, [open, isExisting, selectedCompany, team.accountOwners, team.technicalOwners]);

  // Registered companies dropdown for existing-customer intake
  useEffect(() => {
    if (!open || !isExisting) return undefined;
    const controller = new AbortController();
    const q = companySearch.trim();
    const id = setTimeout(
      async () => {
        setCompaniesLoading(true);
        const { data, error } = await fetchCompanies({
          q: q || undefined,
          page: 1,
          limit: 100,
          signal: controller.signal
        });
        if (controller.signal.aborted) return;
        setCompaniesLoading(false);
        if (error) {
          setCompanies([]);
          showToast('Could not load companies', 'error');
          return;
        }
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        setCompanies(results.map(mapCompanyOption));
      },
      q ? 250 : 0
    );
    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [open, isExisting, companySearch]);

  // Block duplicate open records — by email (pilot) or company (existing)
  useEffect(() => {
    if (!open) {
      setDuplicateCheckPending(false);
      return undefined;
    }

    if (isExisting) {
      if (!selectedCompany?.id) {
        setBlockingRecord(null);
        setDuplicateCheckPending(false);
        return undefined;
      }
      setDuplicateCheckPending(true);
      const controller = new AbortController();
      const id = setTimeout(async () => {
        const { data } = await fetchScopeRecords({
          q: selectedCompany.name,
          signal: controller.signal
        });
        if (controller.signal.aborted) return;
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        const match = results.map(mapRecordListRow).find(r => {
          const sameCompany =
            String(r.companyId) === String(selectedCompany.id) ||
            (r.company || '').toLowerCase() === selectedCompany.name.toLowerCase();
          if (!sameCompany) return false;
          if (r.recordType && r.recordType !== 'existing_customer') return false;
          // One open record per company — anything not yet Submitted
          return r.state !== 'Submitted';
        });
        setBlockingRecord(match || null);
        setDuplicateCheckPending(false);
      }, 300);
      return () => {
        controller.abort();
        clearTimeout(id);
      };
    }

    const email = (form.email || '').trim().toLowerCase();
    if (!email || !isValidEmail(email)) {
      setBlockingRecord(null);
      setDuplicateCheckPending(false);
      return undefined;
    }
    setDuplicateCheckPending(true);
    const controller = new AbortController();
    const id = setTimeout(async () => {
      const { data } = await fetchScopeRecords({ q: email, signal: controller.signal });
      if (controller.signal.aborted) return;
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      const match = results.map(mapRecordListRow).find(r => r.email.toLowerCase() === email && r.state !== 'Submitted');
      setBlockingRecord(match || null);
      setDuplicateCheckPending(false);
    }, 350);
    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [open, isExisting, selectedCompany, form.email]);

  const ccParts = useMemo(
    () =>
      (form.cc || '')
        .split(',')
        .map(x => x.trim())
        .filter(Boolean)
        .map(email => ({ email, valid: isValidEmail(email) })),
    [form.cc]
  );
  const ccBad = ccParts.filter(p => !p.valid).map(p => p.email);
  const emailValid = !!(form.email || '').trim() && isValidEmail((form.email || '').trim());

  let footerHint = 'Creating sends the magic link immediately — no login for the customer.';
  if (ccBad.length) footerHint = 'Fix the CC addresses before sending.';
  else if (isExisting && !selectedCompany) footerHint = 'Pick a registered company to continue.';
  else if (!emailValid) footerHint = 'Enter the customer email to continue.';
  else if (duplicateCheckPending) footerHint = 'Checking for an existing open record…';
  else if (blockingRecord) footerHint = 'Resolve the existing link before creating another.';
  else if (!sendEmails) footerHint = 'Emails are off — this saves the record without notifying the customer.';

  let ccHint = 'They get the invite and the submission notification.';
  if (ccBad.length === 1) ccHint = `${ccBad[0]} is not a valid email`;
  else if (ccBad.length > 1) ccHint = `${ccBad.length} addresses are not valid emails: ${ccBad.join(', ')}`;
  else if (ccParts.length) ccHint = `${ccParts.length} recipient${ccParts.length > 1 ? 's' : ''} will be copied`;

  if (!open) return null;

  const handleClose = () => {
    resetDrawer();
    onClose && onClose();
  };

  const pickCompany = company => {
    if (!company) return;
    setSelectedCompany(company);
    set({
      companyId: company.id,
      company: company.name,
      email: company.adminEmail || '',
      accountOwner: resolveRosterValue(company.accountOwnerPerson, team.accountOwners),
      technicalOwner: resolveRosterValue(company.technicalOwnerPerson, team.technicalOwners)
    });
  };

  const clearCompany = () => {
    setSelectedCompany(null);
    set({ companyId: null, company: '', email: '', accountOwner: '', technicalOwner: '' });
  };

  const onCompanySelect = id => {
    const company = companies.find(c => String(c.id) === String(id));
    if (company) pickCompany(company);
  };

  const switchType = nextType => {
    if (nextType === form.recordType) return;
    setBlockingRecord(null);
    setSelectedCompany(null);
    setCompanySearch('');
    set({
      recordType: nextType,
      email: '',
      company: '',
      companyId: null,
      ae: '',
      fde: '',
      accountOwner: '',
      technicalOwner: ''
    });
  };

  const doSubmit = async ({ sendEmail }) => {
    if (blockingRecord || duplicateCheckPending) return;
    setSubmitting(true);
    setFieldErrors({});
    const body = isExisting
      ? {
          company_id: form.companyId,
          company_name: form.company.trim(),
          customer_email: form.email.trim(),
          cc_emails: ccParts.filter(p => p.valid).map(p => p.email),
          record_type: 'existing_customer',
          is_pilot: false,
          ...accountOwnerFieldBody(accountOwnerItems, form.accountOwner),
          technical_csm_id: form.technicalOwner || undefined,
          send_email: sendEmail,
          exclusions: []
        }
      : {
          company_name: form.company.trim(),
          customer_email: form.email.trim(),
          cc_emails: ccParts.filter(p => p.valid).map(p => p.email),
          record_type: 'pilot',
          is_pilot: true,
          account_executive_id: form.ae || undefined,
          fde_id: form.fde || undefined,
          send_email: sendEmail,
          exclusions: []
        };
    const { data, error } = await createIntakeRecord(body);
    setSubmitting(false);
    if (error) {
      setFieldErrors(parseFieldErrors(error));
      const detail = typeof error === 'string' ? error : error.detail;
      if (detail) showToast(detail, 'error');
      return;
    }
    showToast(sendEmail ? `Link sent to ${body.customer_email}` : `Draft saved for ${body.customer_email}`, 'success');
    onSend && onSend(data);
    resetDrawer();
  };

  const hasRequiredForSave = isExisting
    ? emailValid && !!form.companyId && !!form.accountOwner
    : emailValid && !!(form.company || '').trim() && !!form.ae;

  const saveDisabled =
    !hasRequiredForSave || !!blockingRecord || duplicateCheckPending || submitting || ccBad.length > 0;
  const sendDisabled = saveDisabled || !sendEmails;

  const handleSave = () => {
    if (saveDisabled) return;
    doSubmit({ sendEmail: false });
  };

  const handleSaveAndSend = () => {
    if (sendDisabled) return;
    doSubmit({ sendEmail: true });
  };

  const handleOpenExisting = () => {
    if (!blockingRecord) return;
    onOpenExisting && onOpenExisting(blockingRecord.id);
    resetDrawer();
  };

  return (
    <>
      <S.Backdrop onClick={handleClose} />
      <S.Drawer>
        <S.Header>
          <Typography variant='subtitle1-medium'>New intake</Typography>
          <S.Close onClick={handleClose}>×</S.Close>
        </S.Header>

        <S.Body>
          <S.Field>
            <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
              Record type
            </Typography>
            <S.TypeStack>
              {RECORD_TYPES.map(t => {
                const selected = form.recordType === t.id;
                const Type = selected ? S.TypeOn : S.TypeOff;
                return (
                  <Type key={t.id} type='button' onClick={() => switchType(t.id)}>
                    <S.TypeTitle>{t.title}</S.TypeTitle>
                    <S.TypeBody>{t.body}</S.TypeBody>
                  </Type>
                );
              })}
            </S.TypeStack>
          </S.Field>

          {isExisting ? (
            <>
              <S.Field>
                <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                  Company
                </Typography>
                <Select
                  size='small'
                  variant='std-filled'
                  items={companyItems}
                  value={form.companyId == null || form.companyId === '' ? '' : String(form.companyId)}
                  onChange={onCompanySelect}
                  onClear={clearCompany}
                  allowClear
                  allowClearIcon
                  isLoading={companiesLoading && !companies.length}
                  placeholder={companiesLoading && !companies.length ? 'Loading…' : 'Select registered company'}
                  searchProps={{
                    onSearch: value => setCompanySearch(typeof value === 'string' ? value : value?.target?.value || '')
                  }}
                  noDataText={companiesLoading ? 'Loading…' : 'No companies found'}
                />
                {(fieldErrors.company_id || fieldErrors.company_name) && (
                  <S.FieldErrorText>{fieldErrors.company_id || fieldErrors.company_name}</S.FieldErrorText>
                )}
              </S.Field>

              {blockingRecord && (
                <S.BlockedCard>
                  <div>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      This company already has an open scope record
                    </Typography>
                    <Typography variant='body4' color='#4D4D4D' sx={{ lineHeight: 1.5 }}>
                      One open record per registered company. Open it to continue — or pick a different company.
                    </Typography>
                  </div>
                  <S.BlockedInner>
                    <S.BlockedInnerLeft>
                      <Typography variant='body3' sx={{ fontWeight: 500 }}>
                        {blockingRecord.company}
                      </Typography>
                      <Typography variant='body4' color='#808080'>
                        {blockingRecord.email}
                        {blockingRecord.created ? ` · created ${blockingRecord.created}` : ''}
                      </Typography>
                    </S.BlockedInnerLeft>
                    <StateLozenge state={blockingRecord.state} />
                  </S.BlockedInner>
                  <S.BlockedActions>
                    <Button variant='outlined' color='primary' size='medium' onClick={handleOpenExisting}>
                      Open record
                    </Button>
                  </S.BlockedActions>
                </S.BlockedCard>
              )}

              <S.Field>
                <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                  Customer email
                </Typography>
                <Input
                  size='large'
                  name='customer_email'
                  placeholder='Defaults to the company admin email'
                  value={form.email}
                  onChange={e => set({ email: e.target.value })}
                  error={!!fieldErrors.customer_email}
                  inputProps={{ autoComplete: 'email' }}
                />
                {fieldErrors.customer_email && <S.FieldErrorText>{fieldErrors.customer_email}</S.FieldErrorText>}
              </S.Field>

              <S.FieldPair>
                <S.Field>
                  <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                    Account owner
                  </Typography>
                  <Select
                    size='small'
                    variant='std-filled'
                    items={accountOwnerItems}
                    value={form.accountOwner ? String(form.accountOwner) : ''}
                    onChange={v => set({ accountOwner: v == null || v === '' ? '' : String(v) })}
                    placeholder={teamLoading ? 'Loading…' : 'Select KAM / EAM'}
                    searchProps={{}}
                  />
                  {fieldErrors.kam_id && <S.FieldErrorText>{fieldErrors.kam_id}</S.FieldErrorText>}
                </S.Field>
                <S.Field>
                  <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                    Technical owner
                  </Typography>
                  <Select
                    size='small'
                    variant='std-filled'
                    items={technicalOwnerItems}
                    value={form.technicalOwner ? String(form.technicalOwner) : ''}
                    onChange={v => set({ technicalOwner: v == null || v === '' ? '' : String(v) })}
                    onClear={() => set({ technicalOwner: '' })}
                    allowClear
                    allowClearIcon
                    placeholder={teamLoading ? 'Loading…' : 'Select Technical CSM (optional)'}
                    searchProps={{}}
                  />
                  {fieldErrors.technical_csm_id && <S.FieldErrorText>{fieldErrors.technical_csm_id}</S.FieldErrorText>}
                </S.Field>
              </S.FieldPair>
            </>
          ) : (
            <>
              <S.Field>
                <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                  Customer email
                </Typography>
                <Input
                  size='large'
                  name='customer_email'
                  placeholder='name@company.com'
                  value={form.email}
                  onChange={e => set({ email: e.target.value })}
                  error={!!fieldErrors.customer_email}
                  inputProps={{ autoComplete: 'email' }}
                />
                {fieldErrors.customer_email && <S.FieldErrorText>{fieldErrors.customer_email}</S.FieldErrorText>}
              </S.Field>

              <EmailsToggle on={sendEmails} onToggle={checked => set({ sendEmails: checked })} />

              {blockingRecord && (
                <S.BlockedCard>
                  <div>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      This email already has an active scope link
                    </Typography>
                    <Typography variant='body4' color='#4D4D4D' sx={{ lineHeight: 1.5 }}>
                      One active link per customer email. Open the existing one to continue — or change the email to
                      send a new link.
                    </Typography>
                  </div>
                  <S.BlockedInner>
                    <S.BlockedInnerLeft>
                      <Typography variant='body3' sx={{ fontWeight: 500 }}>
                        {blockingRecord.company}
                      </Typography>
                      <Typography variant='body4' color='#808080'>
                        created {blockingRecord.created}
                        {blockingRecord.sinceSent ? ` · ${blockingRecord.sinceSent}` : ''}
                      </Typography>
                    </S.BlockedInnerLeft>
                    <StateLozenge state={blockingRecord.state} />
                  </S.BlockedInner>
                  <S.BlockedActions>
                    <Button variant='outlined' color='primary' size='medium' onClick={handleOpenExisting}>
                      Open existing link
                    </Button>
                  </S.BlockedActions>
                </S.BlockedCard>
              )}

              <S.Field>
                <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                  Prospect company
                </Typography>
                <Input
                  size='large'
                  name='company_name'
                  placeholder='As they write it on their bids'
                  value={form.company}
                  onChange={e => set({ company: e.target.value })}
                  error={!!fieldErrors.company_name}
                  inputProps={{ autoComplete: 'organization' }}
                />
                {fieldErrors.company_name && <S.FieldErrorText>{fieldErrors.company_name}</S.FieldErrorText>}
              </S.Field>

              <S.FieldPair>
                <S.Field>
                  <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                    Account executive
                  </Typography>
                  <Select
                    size='small'
                    variant='std-filled'
                    items={team.ae}
                    value={form.ae}
                    onChange={v => set({ ae: v })}
                    placeholder={teamLoading ? 'Loading…' : 'Select AE'}
                    searchProps={{}}
                  />
                  {fieldErrors.account_executive_id && (
                    <S.FieldErrorText>{fieldErrors.account_executive_id}</S.FieldErrorText>
                  )}
                </S.Field>
                <S.Field>
                  <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                    Forward-deployed estimator
                  </Typography>
                  <Select
                    size='small'
                    variant='std-filled'
                    items={team.se}
                    value={form.fde}
                    onChange={v => set({ fde: v })}
                    placeholder={teamLoading ? 'Loading…' : 'Select FDE (optional)'}
                    searchProps={{}}
                  />
                  {fieldErrors.fde_id && <S.FieldErrorText>{fieldErrors.fde_id}</S.FieldErrorText>}
                </S.Field>
              </S.FieldPair>
            </>
          )}

          <S.Field>
            <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
              CC recipients
            </Typography>
            <Input
              size='large'
              name='cc_emails'
              placeholder='ops@attentive.ai, pm@customer.com'
              value={form.cc}
              onChange={e => set({ cc: e.target.value })}
              error={ccBad.length > 0 || !!fieldErrors.cc_emails}
              inputProps={{ autoComplete: 'off' }}
            />
            {ccBad.length ? (
              <S.CcHintError>{fieldErrors.cc_emails || ccHint}</S.CcHintError>
            ) : (
              <S.CcHint>{fieldErrors.cc_emails || ccHint}</S.CcHint>
            )}
            {ccParts.length > 0 && (
              <S.ChipRow>
                {ccParts.map(p => {
                  const Chip = p.valid ? S.ChipValid : S.ChipInvalid;
                  return <Chip key={p.email}>{p.email}</Chip>;
                })}
              </S.ChipRow>
            )}
          </S.Field>

          {isExisting && <EmailsToggle on={sendEmails} onToggle={checked => set({ sendEmails: checked })} />}
        </S.Body>

        <S.Footer>
          <Typography variant='body4' color='#808080'>
            {footerHint}
          </Typography>
          <S.FooterActions>
            {sendEmails ? (
              <Button
                variant='contained'
                color='primary'
                disabled={sendDisabled}
                loading={submitting}
                onClick={handleSaveAndSend}
                size='medium'>
                Save & send
              </Button>
            ) : (
              <Button
                variant={sendEmails ? 'outlined' : 'contained'}
                color='primary'
                disabled={saveDisabled}
                loading={submitting}
                onClick={handleSave}>
                Save
              </Button>
            )}
          </S.FooterActions>
        </S.Footer>
      </S.Drawer>
    </>
  );
};

export default IntakeDrawer;
